import { AddMatrixControls, AddVectorControls } from "./AddControls";
import {
  MatrixAnimationProgressProvider,
  MatrixCard,
} from "./cards/MatrixCard";
import { useEffect, useRef } from "react";
import type { AnimationSource } from "../hooks/use-animation-progress";
import type { Equation } from "@src/features/equation-editor/equation";
import { DiagnosticBadge } from "./cards/DiagnosticField";
import type { EquationEditorActions } from "../editor-actions";
import { InputVectorMatrix } from "./cards/InputVectorMatrix";
import type { ReactNode } from "react";
import { TransformedVectorMatrix } from "./TransformedVectorMatrix";
import type { Translate } from "@src/i18n/translate";
import { diagnosticMessage } from "../lib/diagnostic-message";
import { getEquationViewModel } from "@src/features/equation-editor/lib/view-model";
import { useAnimationProgress } from "../hooks/use-animation-progress";
import { useReorder } from "../hooks/use-reorder";
import type { Dimension } from "@src/math/types";
import type { EquationViewModel } from "../lib/view-model";

export interface EquationEditorViewProps<D extends Dimension> {
  equation: Equation<D>;
  animationSource: AnimationSource;
  actions: EquationEditorActions;
  t: Translate;
}

export function EquationEditorView<D extends Dimension>({
  equation,
  animationSource,
  actions,
  t,
}: EquationEditorViewProps<D>) {
  const equationRef = useRef<HTMLElement>(null);
  const viewModel = getEquationViewModel(equation);
  const reorder = useReorder({ equation, actions, t });
  const hasVectors = equation.vectors.length > 0;
  const hasResults = equation.evaluation.value.vectors.length > 0;
  const trayDiagnosticMessages = viewModel.trayDiagnostics.map((diagnostic) =>
    diagnosticMessage(t, diagnostic.code),
  );
  const deleteItem = (kind: "matrix" | "vector", id: string): void => {
    const deletedIndex = equationItemIndex(equation, kind, id);
    if (deletedIndex < 0) return;
    const focusTarget = deleteFocusTarget(
      equationRef.current,
      kind,
      deletedIndex,
    );
    actions.deleteItem(kind, id);
    setTimeout(() => {
      const target = focusTarget?.isConnected
        ? focusTarget
        : equationRef.current?.querySelector<HTMLElement>(
            equationAddSelector(kind),
          );
      target?.focus();
    }, 0);
  };

  useEffect(() => {
    const dismissOpenMenus = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      equationRef.current
        ?.querySelectorAll<HTMLDetailsElement>("details.popup-menu[open]")
        .forEach((menu) => {
          if (!menu.contains(target)) menu.open = false;
        });
    };
    document.addEventListener("pointerdown", dismissOpenMenus);
    return () => {
      document.removeEventListener("pointerdown", dismissOpenMenus);
    };
  }, []);

  return (
    <>
      <section
        ref={equationRef}
        className="equation-tray"
        aria-label={t("transformationEquation")}
      >
        {trayDiagnosticMessages.length > 0 && (
          <div className="equation-tray-diagnostic">
            <DiagnosticBadge
              id={`equation-${String(equation.dimension)}-diagnostic`}
              messages={trayDiagnosticMessages}
              indicatorLabel={t("evaluationStatusDetails")}
            />
          </div>
        )}
        <div
          className={equationRowClassName(hasResults)}
          data-tray-diagnostic={trayDiagnosticMessages.length > 0 || undefined}
        >
          <section
            className="equation-left"
            aria-label={t("inputMatricesAndVectors")}
          >
            <ol className="matrix-stack" aria-label={t("matrices")}>
              <li className="matrix-add-item">
                <AddMatrixControls
                  equation={equation}
                  t={t}
                  onAdd={actions.addMatrix}
                  onPreset={actions.addMatrixPreset}
                  onInverse={actions.addInverseTransform}
                />
              </li>
              <AnimatedMatrixCards
                animationSource={animationSource}
                equation={equation}
                viewModel={viewModel}
                t={t}
                actions={actions}
                reorder={reorder}
                onDelete={deleteItem}
              />
            </ol>
            {hasVectors ? (
              <InputVectorMatrix
                equation={equation}
                viewModel={viewModel}
                t={t}
                onAdd={actions.addVector}
                onEigenbasis={actions.addEigenbasis}
                onEigenvector={actions.addEigenvector}
                onDelete={(id) => {
                  deleteItem("vector", id);
                }}
                onCoordinateChange={actions.setVectorCoordinate}
                onKeyDown={reorder.keyboardReorder}
                onDragStart={(id, preview) => {
                  reorder.startDrag("vector", id, preview);
                }}
                draggingId={draggingVectorId(reorder.dragging)}
                dropTarget={vectorDropTarget(reorder.dropTarget)}
                onDragOver={(event, id) => {
                  reorder.dragOver(event, "vector", id);
                }}
                onDragEnd={reorder.endDrag}
                onDrop={reorder.drop}
              />
            ) : (
              <AddVectorControls
                equation={equation}
                t={t}
                onAdd={actions.addVector}
                onEigenbasis={actions.addEigenbasis}
                onEigenvector={actions.addEigenvector}
                capabilities={viewModel.capabilities}
              />
            )}
          </section>
          {hasResults && (
            <>
              <math
                className="equation-equals"
                data-stale={equation.evaluation.kind !== "current" || undefined}
                aria-describedby={
                  equation.evaluation.kind !== "current"
                    ? "stale-result-status"
                    : undefined
                }
                aria-label={t("equals")}
              >
                <mo>=</mo>
              </math>
              <TransformedVectorMatrix equation={equation} t={t} />
            </>
          )}
        </div>
      </section>
      <p id="reorder-instructions" className="visually-hidden">
        {t("reorderInstructions")}
      </p>
      <div className="visually-hidden" role="status" aria-live="polite">
        {reorder.announcement}
      </div>
    </>
  );
}

function equationItemIndex<D extends Dimension>(
  equation: Equation<D>,
  kind: "matrix" | "vector",
  id: string,
): number {
  const items = kind === "matrix" ? equation.matrices : equation.vectors;
  return items.findIndex((item) => item.id === id);
}

function deleteFocusTarget(
  root: HTMLElement | null,
  kind: "matrix" | "vector",
  deletedIndex: number,
): HTMLElement | null | undefined {
  const items = Array.from(
    root?.querySelectorAll<HTMLElement>(equationItemSelector(kind)) ?? [],
  );
  const sibling =
    items.find((_, index) => index === deletedIndex + 1) ??
    items.find((_, index) => index === deletedIndex - 1);
  if (sibling) return sibling;
  return root?.querySelector<HTMLElement>(equationAddSelector(kind));
}

function equationItemSelector(kind: "matrix" | "vector"): string {
  return kind === "matrix" ? ".matrix-item" : ".vector-column-label";
}

function equationAddSelector(kind: "matrix" | "vector"): string {
  return kind === "matrix"
    ? ".matrix-add-button .preset-main"
    : ".vector-add-button .preset-main";
}

function AnimatedMatrixCards<D extends Dimension>({
  animationSource,
  equation,
  viewModel,
  t,
  actions,
  reorder,
  onDelete,
}: {
  animationSource: AnimationSource;
  equation: Equation<D>;
  viewModel: EquationViewModel;
  t: Translate;
  actions: Pick<
    EquationEditorActions,
    "deleteItem" | "setMatrixEntry" | "setMatrixDuration"
  >;
  reorder: ReturnType<typeof useReorder<D>>;
  onDelete: (kind: "matrix" | "vector", id: string) => void;
}) {
  return (
    <AnimatedMatrixProgress
      animationSource={animationSource}
      equation={equation}
    >
      {equation.matrices.map((matrix) => (
        <MatrixCard
          key={matrix.id}
          matrix={matrix}
          equation={equation}
          viewModel={viewModel}
          t={t}
          onDelete={() => {
            onDelete("matrix", matrix.id);
          }}
          onEntryChange={actions.setMatrixEntry}
          onDurationChange={actions.setMatrixDuration}
          onKeyDown={(event) => {
            reorder.keyboardReorder(event, "matrix", matrix.id);
          }}
          onDragStart={() => {
            reorder.startDrag("matrix", matrix.id);
          }}
          dragging={isDraggingMatrix(reorder.dragging, matrix.id)}
          dropPosition={matrixDropPosition(reorder.dropTarget, matrix.id)}
          onDragOver={(event) => {
            reorder.dragOver(event, "matrix", matrix.id);
          }}
          onDragEnd={reorder.endDrag}
          onDrop={(event) => {
            reorder.drop(event, "matrix", matrix.id);
          }}
        />
      ))}
    </AnimatedMatrixProgress>
  );
}

function AnimatedMatrixProgress<D extends Dimension>({
  animationSource,
  equation,
  children,
}: {
  animationSource: AnimationSource;
  equation: Equation<D>;
  children: ReactNode;
}) {
  const progress = useAnimationProgress(animationSource, equation);
  return (
    <MatrixAnimationProgressProvider progress={progress}>
      {children}
    </MatrixAnimationProgressProvider>
  );
}

function equationRowClassName(hasResults: boolean): string {
  return hasResults ? "equation-row " : "equation-row equation-row-input-only";
}

function isDraggingMatrix(
  dragging: { kind: "matrix" | "vector"; id: string } | null,
  matrixId: string,
): boolean {
  return dragging?.kind === "matrix" && dragging.id === matrixId;
}

function matrixDropPosition(
  target: {
    kind: "matrix" | "vector";
    id: string;
    side: "before" | "after";
  } | null,
  matrixId: string,
): "before" | "after" | undefined {
  if (target?.kind !== "matrix" || target.id !== matrixId) return undefined;
  return target.side;
}

function draggingVectorId(
  dragging: { kind: "matrix" | "vector"; id: string } | null,
): string | undefined {
  return dragging?.kind === "vector" ? dragging.id : undefined;
}

function vectorDropTarget<T extends { kind: "matrix" | "vector" }>(
  target: T | null,
): T | null {
  return target?.kind === "vector" ? target : null;
}
